import { UIRouterGlobals } from "@uirouter/angular";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import equal from "fast-deep-equal";
import { BehaviorSubject, Observable, combineLatest, debounceTime, first, forkJoin, map, of, pairwise, startWith, switchMap, take, takeUntil, tap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import dayjs from "@gtmhub/dayjs";
import { IIndicator } from "@gtmhub/error-handling";
import { DEFAULT_LIMIT, IMetricMethodologySettingsUI, OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { IGoal } from "@gtmhub/goals/models";
import { IInsightSelectorData } from "@gtmhub/insights";
import { IMetricPrediction } from "@gtmhub/insights/services/auto-fields.service";
import { OKRsEventType } from "@gtmhub/okrs/events";
import { generateMetricPredictionAnalytics } from "@gtmhub/okrs/metrics/auto-fields-util";
import { MetricDeadLineEnum, metricFormStrings } from "@gtmhub/okrs/metrics/utils";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { IUnitFormatting } from "@gtmhub/shared/models";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { IReduxTeam, ITeamsStoreState } from "@gtmhub/teams";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { toIdMap } from "@gtmhub/util";
import { Flavor, recommendation } from "@webapp/analytics/models/analytics.model";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { TracingService } from "@webapp/core/tracing/services/tracing.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { AutoFieldsFacade } from "@webapp/data-story/services/auto-fields-facade/auto-fields-facade.service";
import { AutoInsightsFacade } from "@webapp/data-story/services/auto-insights-facade/auto-insights-facade.service";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { localize } from "@webapp/localization/utils/localization.utils";
import { GoalsFacade } from "@webapp/okrs/goals/services/goals-facade.service";
import {
  FocusField,
  MetricFormParams,
  MetricManualTypeEnum,
  MetricPredictedField,
  MetricPredictedFields,
  PiSuggestionsType,
} from "@webapp/okrs/metrics/components/create-metric-form/create-metric-form.models";
import {
  GenerateMetricFromFormOptions,
  createNewMetricFromForm,
  createOnlyUpdatedFieldsMetric,
  generateDefaultMetricValues,
  getDatesRespectingMetric,
} from "@webapp/okrs/metrics/components/create-metric-form/create-metric-form.utils";
import { criticalValidator, initialValueValidator } from "@webapp/okrs/metrics/components/create-metric-form/validators/validators";
import { MetricTypeSelectorChange, MetricTypeSelectorOption } from "@webapp/okrs/metrics/components/metric-type-selector/metric-type-selector.models";
import { Deadline, Metric, MetricFormEventType, MetricTargetOperator } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";
import { generateInsightSelectorData, generateOptions, isFieldPartOfWorkflowUtil, sendTrackingAnalyticsV2 } from "@webapp/okrs/utils/metric.utils";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { SuggestionDrawerIndicatorStatePropagator } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/utils/state-propagator";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiSuggestionSharedMetricStateService } from "@webapp/platform-intelligence/shared/services/pi-suggestion-shared-metric-state.service";
import { ISessionsIdMap, Session } from "@webapp/sessions/models/sessions.model";
import { PeopleSelectorRequest } from "@webapp/shared/components/people-selector/models/models";
import { EditInsightService } from "@webapp/shared/insights/edit-modal/edit-insight.service";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";

const GOAL_PARAMS = {
  fields: "private,id,sessionId,ownerIds,locked,workflow{status},metrics{id,name,description},name,description,currentUserAllowedActions",
};

type ExtractFormControl<T> = {
  [K in keyof T]: T[K] extends FormControl<infer U> ? U : T[K];
};

export type FormFieldsValues = ExtractFormControl<FormFields>;

export interface FormFields {
  name: FormControl<string>;
  type: FormControl<MetricTypeSelectorChange>;
  format: FormControl<IUnitFormatting>;
  target: FormControl<number>;
  useCritical: FormControl<boolean>;
  critical: FormControl<number>;
  initialValue: FormControl<number>;
  automation: FormControl<IInsightSelectorData>;
  description: FormControl<string>;
  ownerIds: FormControl<string[]>;
  deadline: FormControl<Deadline>;
  customFields: FormControl<CfMap>;
}

@Component({
  selector: "create-metric-form",
  templateUrl: "./create-metric-form.component.html",
  styleUrls: ["./create-metric-form.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
})
export class CreateMetricFormComponent implements OnInit {
  // This is temporary solution until the migration is done
  @Output() public readonly dismiss: EventEmitter<null> = new EventEmitter();

  private isAchievedOrNotDisabled: boolean;
  private isAchievedOrNotMetricType: boolean;
  public session: Session;
  public goal: IGoal;
  public metric: Metric;
  public metricsLimitReached = false;
  public allowedActions: string[] = [];
  public isEdit = false;
  public isPrefilled = false;
  public actual: number;
  private stateParams: MetricFormParams;
  public metricMethodologySettings: IMetricMethodologySettingsUI & { metricsPerGoalLimit: number };
  public hasPermissionsToDisplayInsightSelector: boolean;
  public peopleSelectorRequest: PeopleSelectorRequest;

  public indicators: {
    loading?: IIndicator;
    saving?: IIndicator;
  };

  // Auto fields suggestions
  public prediction: IMetricPrediction;
  public shouldPredict = {
    format: true,
    targetOperator: true,
    target: true,
    // will be set to 0 in webapp if target is predicted
    // and operator is not should decrease to
    initialValue: true,
  };

  // Focus after suggestion is accepted
  public focus$ = new BehaviorSubject("shouldFocusTitleInput");

  // Redux
  public assigneesIdMap: Record<string, Assignee>;
  public sessionsIdMap: ISessionsIdMap;
  public teams: IReduxTeam[];

  // Feature toggles
  public dynamicKrsFeatureEnabled: boolean;
  public autoFieldsFeatureEnabled: boolean;
  public isSuggestingKRInCreateKRFormEnabled: boolean;

  // Datepicker options
  public datepickerOptions: {
    dueDatePicker: {
      minDate: dayjs.Dayjs;
      maxDate: dayjs.Dayjs;
    };
    softDueDatePicker: {
      minDate: dayjs.Dayjs;
      maxDate: dayjs.Dayjs;
    };
  };

  // Platform Intelligence
  public flowId: string;
  public drawerStatePropagator: SuggestionDrawerIndicatorStatePropagator;
  public shouldDisplaySuggestionDrawer = false;
  public hasAcceptedTitleSuggestion: boolean;
  public hasAcceptedDescriptionSuggestion: boolean;

  // Form
  public typeOptions: MetricTypeSelectorOption[];
  public form: FormGroup<FormFields>;

  constructor(
    private tracingService: TracingService,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private currentUserService: CurrentUserService,
    private accountService: AccountService,
    private uIRouterGlobals: UIRouterGlobals,
    private metricsFacade: MetricsFacade,
    private goalFacade: GoalsFacade,
    private autoFieldsFacade: AutoFieldsFacade,
    private autoInsightsFacade: AutoInsightsFacade,
    private broadcastService: BroadcastService,
    private destroyService: DestroyService,
    private cdr: ChangeDetectorRef,
    private modalService: UiModalService,
    private analyticsService: AnalyticsService,
    private featureTogglesFacade: FeatureTogglesFacade,
    private editInsightService: EditInsightService,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    public permissionsFacade: PermissionsFacade,
    private piSuggestionSharedMetricStateService: PiSuggestionSharedMetricStateService
  ) {}

  public ngOnInit(): void {
    this.indicators = { loading: { progress: true } };

    this.initForm();

    this.flowId = uuidv4();
    this.drawerStatePropagator = new SuggestionDrawerIndicatorStatePropagator();
    this.dynamicKrsFeatureEnabled = this.accountService.featureAvailable("insightboards.dynamic-kr");
    this.autoFieldsFeatureEnabled = this.accountService.featureAvailable("autofields");
    this.stateParams = this.uIRouterGlobals.params as unknown as MetricFormParams;

    combineLatest({
      isAchievedOrNotDisabled: this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.AchievedOrNotMetricType).pipe(first()),
      isPiEnabled: this.checkSuggestionDrawerWhenCreateKrFeatureToggle(),
      redux: this.loadRedux$().pipe(
        takeUntil(this.destroyService),
        switchMap(() => this.loadMetric$()),
        switchMap(() => this.loadGoalIfMissing$()),
        switchMap(() => this.loadParams$())
      ),
    }).subscribe({
      next: ({ isAchievedOrNotDisabled }) => {
        this.setAllowedActions();
        this.isAchievedOrNotDisabled = isAchievedOrNotDisabled;
        this.isAchievedOrNotMetricType = this.metric?.manualType === "boolean";

        this.setFormValues();

        this.actual = this.metric.actual;

        this.setSharedMetricServiceMetric();

        delete this.indicators.loading;
      },
      error: (err) => (this.indicators.loading = { error: err }),
    });
  }

  private initForm(): void {
    this.form = new FormGroup<FormFields>(
      {
        name: new FormControl<string>(null, [Validators.required]),
        type: new FormControl<MetricTypeSelectorChange>(null),
        format: new FormControl<IUnitFormatting>(null),
        target: new FormControl<number>(null, [Validators.required]),
        useCritical: new FormControl<boolean>(false),
        critical: new FormControl<number>(null, []),
        initialValue: new FormControl<number>(null, [Validators.required]),
        automation: new FormControl<IInsightSelectorData>(null),
        description: new FormControl<string>(null),
        ownerIds: new FormControl<string[]>(null, [Validators.required]),
        deadline: new FormControl<Deadline>({
          useDeadline: null,
          dueDate: null,
          softDueDate: null,
        }),
        customFields: new FormControl(null),
      },
      { validators: [initialValueValidator(), criticalValidator()] }
    );
  }

  private setFormValues(): void {
    // Options
    this.typeOptions = generateOptions(this.metric, { hideAchievedOrNotType: !this.isAchievedOrNotMetricType && this.isAchievedOrNotDisabled });

    // Values
    this.form.patchValue({
      name: this.metric.name,
      type: { manualType: this.metric.manualType, targetOperator: this.metric.targetOperator },
      format: angular.copy(this.metric.format),
      target: this.metric.target,
      useCritical: this.metric.critical !== null || this.metric.critical === 0,
      critical: this.metric.critical,
      initialValue: this.metric.initialValue,
      ownerIds: this.getMetricOwnerIds(),
      description: this.metric.description,
      customFields: angular.copy(this.metric.customFields),
    });

    // Sets dates
    this.disableDatesOutsideSessionTimeframe();

    // Sets automation
    if (this.hasPermissionsToDisplayInsightSelector) {
      const newInsightName = this.editInsightService.getNewInsightName();
      if (newInsightName && newInsightName !== this.metric.insightName) {
        // GVS-27702
        this.form.controls.automation.setValue(generateInsightSelectorData({ ...this.metric, insightName: newInsightName }));
      } else {
        this.form.controls.automation.setValue(generateInsightSelectorData(this.metric));
      }
    }

    // Sets states
    this.disableFieldsOnce();
    this.disableFieldsOnChange();

    // Subscribe to changes in the form values
    this.subscribeToFormChanges();
  }

  private disableFieldsOnce(): void {
    if (this.goal && this.goal.locked && !this.isFieldPartOfWorkflow("name")) {
      this.form.controls.name.disable();
    }

    if (this.metric.sourceMetricId || (this.goal && this.goal.locked && !this.isFieldPartOfWorkflow("target"))) {
      this.form.controls.target.disable();
      this.form.controls.critical.disable();
      this.form.controls.type.disable();
    }

    if (this.metric.sourceMetricId) {
      this.form.controls.useCritical.disable();
      this.form.controls.format.disable();
    }

    if (this.goal && this.goal.locked && !this.isFieldPartOfWorkflow("ownerIds")) {
      this.form.controls.ownerIds.disable();
    }
  }

  private disableFieldsOnChange(): void {
    if (
      this.form.controls.type.value.manualType === "boolean" ||
      !!this.metric.sourceMetricId ||
      (this.goal && this.goal.locked && !this.isFieldPartOfWorkflow("initialValue"))
    ) {
      this.form.controls.initialValue.disable();
    } else {
      this.form.controls.initialValue.enable();
    }

    if (
      this.form.controls.type.value.manualType === "boolean" ||
      !!this.metric.sourceMetricId ||
      (this.goal && this.goal.locked && !this.isFieldPartOfWorkflow("target"))
    ) {
      this.form.controls.target.disable();
    } else {
      this.form.controls.target.enable();
    }
  }

  private subscribeToFormChanges(): void {
    this.form.controls.useCritical.valueChanges.pipe(takeUntil(this.destroyService)).subscribe((value) => {
      // Critical should be set to null if useCritical is false
      // So we can replace its value in the metric on update
      if (value === false) {
        this.form.controls.critical.setValue(null);
      }
    });

    this.form.controls.deadline.valueChanges.pipe(takeUntil(this.destroyService)).subscribe((values) => {
      this.updateDeadlineWithValidDates(values);
    });

    this.form.controls.automation.valueChanges.pipe(takeUntil(this.destroyService), startWith(null), pairwise()).subscribe(([oldValue, newValue]) => {
      this.updateAutomation(oldValue, newValue);
    });

    this.form.controls.ownerIds.valueChanges.pipe(takeUntil(this.destroyService)).subscribe((ids) => {
      if (ids && ids.length > 0) {
        this.setSharedMetricServiceMetric();
      }
    });

    this.form.controls.type.valueChanges.pipe(takeUntil(this.destroyService)).subscribe(() => {
      this.updatePrediction("targetOperator", "type");
      this.disableFieldsOnChange();
    });

    this.form.controls.target.valueChanges.pipe(takeUntil(this.destroyService)).subscribe(() => {
      this.updatePrediction("target");
    });

    this.form.controls.initialValue.valueChanges.pipe(takeUntil(this.destroyService)).subscribe(() => {
      this.updatePrediction("initialValue");
    });

    this.form.controls.format.valueChanges.pipe(takeUntil(this.destroyService)).subscribe(() => {
      this.updatePrediction("format");
    });

    this.form.controls.name.valueChanges.pipe(takeUntil(this.destroyService), debounceTime(1000)).subscribe(() => {
      this.handleTitleChange();
    });
  }

  private updateAutomation(oldAutomation: IInsightSelectorData, automation: IInsightSelectorData): void {
    if (!automation || !automation.insightName) {
      return;
    }

    this.form.controls.type.setValue({
      manualType: "",
      targetOperator: this.form.controls.type.value?.targetOperator,
    });

    if (typeof automation.initialValue === "number" && automation.initialValue !== oldAutomation?.initialValue) {
      this.form.controls.initialValue.setValue(automation.initialValue);
    }

    // set the actual of a new metric
    // because sometimes it may keep the value of insight added via IB and then changed
    if (!this.stateParams.metricId && typeof automation.initialValue === "number") {
      this.actual = automation.initialValue;
    }

    if (automation.formatting && !equal(automation.formatting, oldAutomation?.formatting)) {
      // init metric format again because to prediction sets it to null when we don't have a prediction,
      // if we have prediction we should override it with the data from the insight
      this.form.controls.format.setValue({
        prefix: !automation.formatting.suffix && automation.formatting.unit ? automation.formatting.unit : "",
        suffix: automation.formatting.suffix && automation.formatting.unit ? automation.formatting.unit : "",
        fractionSize: 0,
      });
    }

    // Update options in 'type' dropdown
    this.typeOptions = generateOptions(
      { ...this.metric, insightName: automation.insightName },
      { hideAchievedOrNotType: !this.isAchievedOrNotMetricType && this.isAchievedOrNotDisabled }
    );
  }

  private updateDeadlineWithValidDates(values: Deadline): void {
    if (!this.session) {
      return;
    }

    if (values.useDeadline === MetricDeadLineEnum.NONE) {
      this.setDefaultDatepickerRanges(this.session);
      this.form.controls.deadline.patchValue(this.getDefaultDeadline(), { emitEvent: false });
      return;
    }

    const oneDayBeforeHardDueDate = dayjs(values.dueDate).subtract(1, metricFormStrings.day);
    const isDiffBetweenHardAndSoftDatesInvalid = dayjs(values.dueDate).diff(values.softDueDate) <= 0;

    if (
      isDiffBetweenHardAndSoftDatesInvalid ||
      (values.useDeadline === MetricDeadLineEnum.HARD_DEADLINE && dayjs(values.dueDate).isSameOrBefore(dayjs(values.softDueDate)))
    ) {
      this.form.controls.deadline.patchValue(
        {
          ...values,
          softDueDate: oneDayBeforeHardDueDate.toISOString(),
        },
        { emitEvent: false }
      );
    }

    this.datepickerOptions.softDueDatePicker.maxDate = oneDayBeforeHardDueDate;
  }

  public validateRequiredCustomFields = (requiredCf: { areFilled: boolean }): void => {
    if (requiredCf.areFilled === false) {
      this.form.controls.customFields.setErrors({
        requiredCustomFieldsNotFilled: true,
      });
    }
  };

  private loadRedux$ = (): Observable<void> => {
    const { reduxStore } = reduxStoreContainer;
    const reduxObserver = new ReduxStoreObserver(reduxStore);
    return reduxObserver
      .whenFetched$<IAssigneesStoreState & ISessionsStoreState & ITeamsStoreState>("assignees", "sessions", "teams")
      .pipe(takeUntil(this.destroyService))
      .pipe(
        map(({ assignees, sessions, teams }) => {
          this.assigneesIdMap = assignees.map;
          this.sessionsIdMap = toIdMap(sessions.items);
          this.teams = teams.items;
        })
      );
  };

  private loadMetric$ = (): Observable<{ fields: string }> => {
    return forkJoin([
      this.stateParams.id ? this.goalFacade.getGoal$(this.stateParams.id, GOAL_PARAMS) : of(null),
      this.stateParams.metricId ? this.metricsFacade.getMetric$(this.stateParams.metricId) : of(null),
      this.getHasPermissionsToDisplayInsightSelector(),
    ]).pipe(
      takeUntil(this.destroyService),
      switchMap(([goal, metric, shouldDisplayInsightSelector]) => {
        this.hasPermissionsToDisplayInsightSelector = shouldDisplayInsightSelector;

        if (goal) {
          this.goal = goal;
        }

        this.setMetric(metric);

        return of(null);
      })
    );
  };

  private getHasPermissionsToDisplayInsightSelector(): Observable<boolean> {
    return this.permissionsFacade.hasPermission$("ManageData").pipe(
      take(1),
      takeUntil(this.destroyService),
      map((manageData) => {
        const adminRole = this.currentUserService.userHasRole("admin");

        return !!((this.dynamicKrsFeatureEnabled && manageData) || adminRole);
      })
    );
  }

  public isFieldPartOfWorkflow(fieldName: string): boolean {
    return isFieldPartOfWorkflowUtil(fieldName, this.session);
  }

  private setMetric = (metric: Metric): void => {
    if (this.stateParams.metricId) {
      this.metric = metric;
      this.isEdit = true;
    } else {
      if (this.stateParams.metric) {
        this.metric = angular.copy(this.stateParams.metric);
        this.isPrefilled = true;
      } else {
        this.metric = generateDefaultMetricValues(this.goal.ownerIds, this.stateParams.id);
      }
    }
  };

  private loadGoalIfMissing$ = (): Observable<void> => {
    if (this.goal) {
      return of(null);
    } else {
      // in case if you open edit form and refresh
      return this.goalFacade.getGoal$(this.metric.goalId, GOAL_PARAMS).pipe(
        takeUntil(this.destroyService),
        map((goal) => {
          this.goal = goal;
        })
      );
    }
  };

  private getMetricOwnerIds = (): string[] => {
    if (this.isEdit || this.isPrefilled) {
      return angular.copy(this.metric.ownerIds);
    }

    const metricOwnersPermissions: IMetricMethodologySettingsUI = {
      assignMetricPermissions: this.metricMethodologySettings.assignMetricPermissions,
      multipleOwnersPerMetricDisabled: this.metricMethodologySettings.multipleOwnersPerMetricDisabled,
    };

    return this.filterOwnerIdsByMetricMethodologySettings(metricOwnersPermissions, this.goal.ownerIds);
  };

  private loadParams$(): Observable<void> {
    this.metricsFacade.trackMetricForm(MetricFormEventType.KR_FORM_OPENED, this.metric);

    this.session = this.goal.sessionId && this.goal.sessionId !== "" ? this.sessionsIdMap[this.goal.sessionId] : null;
    this.setMetricMethodologySettings();
    this.checkMetricsLimit();
    this.setPeopleSelectorRequest();
    this.broadcastService.emit("focusTextInput");

    return of(null);
  }

  private setAllowedActions(): void {
    this.allowedActions = this.stateParams.metricId ? this.metric.currentUserAllowedActions : this.goal.currentUserAllowedActions;
  }

  private setMetricMethodologySettings(): void {
    this.metricMethodologySettings = {
      ...this.okrMethodologyFacade.getMethodologySettingsUIForMetricView(this.session),
      metricsPerGoalLimit: this.isEdit ? DEFAULT_LIMIT : this.okrMethodologyFacade.getLimitSetting("metric", this.session?.settings),
    };
  }

  private filterOwnerIdsByMetricMethodologySettings = (permissions: IMetricMethodologySettingsUI, goalOwnerIds: string[]): string[] => {
    let metricOwnerIds = goalOwnerIds || [];

    if (permissions.assignMetricPermissions === "team") {
      metricOwnerIds = goalOwnerIds.filter((id) => this.assigneesIdMap[id].type === "team");
    }

    if (permissions.assignMetricPermissions === "user") {
      metricOwnerIds = goalOwnerIds.filter((id) => this.assigneesIdMap[id].type === "user");
    }

    return permissions.multipleOwnersPerMetricDisabled ? [metricOwnerIds[0]] : metricOwnerIds;
  };

  private checkMetricsLimit = (): void => {
    const metricsCount = this.goal.metrics?.length || -1;

    this.metricsLimitReached = metricsCount >= this.metricMethodologySettings.metricsPerGoalLimit;
  };

  public updateDescription = ($event: string): void => {
    this.form.controls.description.setValue($event);

    this.setSharedMetricServiceMetric();
  };

  public updateFocus = (focus: FocusField): void => {
    this.focus$.next(focus);
  };

  private setPeopleSelectorRequest(): void {
    this.peopleSelectorRequest = {
      ...new PeopleSelectorRequest(),
      type: this.metricMethodologySettings.assignMetricPermissions,
      permissionSetting: { enabled: true, permission: "ManageGoals" },
    };
  }

  public filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.goal.sessionId, customFields);
  };

  public isCustomFieldDisabled = (): boolean => {
    return this.goal.locked && this.isEdit;
  };

  public closeModal = ($event?: KeyboardEvent): void => {
    if (!$event || $event.key === "Enter") {
      this.dismiss.emit();
    }
  };

  private disableDatesOutsideSessionTimeframe(): void {
    if (this.session.start == null || this.session.end == null) {
      return;
    }

    this.setDefaultDatepickerRanges(this.session);

    this.form.controls.deadline.patchValue(getDatesRespectingMetric(this.metric, this.stateParams, this.getDefaultDeadline()), {
      emitEvent: false,
    });
  }

  // WARNING MODAL ON EDIT SECTION START

  public edit = (): void => {
    if (this.isEdit && this.shouldOpenDeadlineWarningModal()) {
      this.openWarningModal();
    } else {
      this.save();
    }
  };

  private openWarningModal = (): void => {
    this.modalService.confirm({
      uiTitle: localize("are_you_sure_you_want_to_change_the_deadline"),
      uiContent: localize("changing_the_deadline_of_cascading_key_result"),
      uiOkText: localize("change"),
      uiOnOk: () => this.save(),
      uiOnCancel: () => {
        this.form.controls.deadline.patchValue(getDatesRespectingMetric(this.metric, this.stateParams, this.getDefaultDeadline()), {
          emitEvent: false,
        });
        this.cdr.detectChanges();
      },
    });
  };

  private shouldOpenDeadlineWarningModal = (): boolean => {
    return (
      this.metric.cascadeType &&
      (this.areDatesChanged(this.metric.dueDate, this.form.controls.deadline.value.dueDate) ||
        this.areDatesChanged(this.metric.softDueDate, this.form.controls.deadline.value.softDueDate) ||
        this.isDeadlineChanged())
    );
  };

  private areDatesChanged = (metricDueDate: string, scopeDueDate: string): boolean => {
    const areDueDatesDifferent: boolean = metricDueDate !== scopeDueDate;

    return (
      (areDueDatesDifferent && metricDueDate === metricFormStrings.default.date && this.isDeadlineChanged()) ||
      (areDueDatesDifferent && metricDueDate !== metricFormStrings.default.date)
    );
  };

  private isDeadlineChanged = (): boolean => {
    const initialDeadline = getDatesRespectingMetric(this.metric, this.stateParams, this.getDefaultDeadline()).useDeadline;
    return initialDeadline !== this.form.controls.deadline.value.useDeadline;
  };

  private getDefaultDeadline(): Deadline {
    return {
      useDeadline: MetricDeadLineEnum.NONE,
      dueDate: this.datepickerOptions.dueDatePicker.maxDate.toISOString(),
      softDueDate: this.datepickerOptions.softDueDatePicker.maxDate.toISOString(),
    };
  }

  private setDefaultDatepickerRanges(session: Session): void {
    this.datepickerOptions = {
      dueDatePicker: {
        minDate: dayjs(session.start).add(1, metricFormStrings.day),
        maxDate: dayjs(session.end).subtract(1, metricFormStrings.day),
      },
      softDueDatePicker: {
        minDate: dayjs(session.start).add(1, metricFormStrings.day),
        maxDate: dayjs(session.end).subtract(2, metricFormStrings.day),
      },
    };
  }

  // WARNING MODAL ON EDIT SECTION END
  // SAVE SECTION START

  public save = (): void => {
    if (this.form.invalid) {
      return;
    }

    this.indicators.saving = { progress: true };

    if (this.isEdit) {
      this.tracingService.traceAction("update_KR", () => {
        this.updateMetric();
      });
    } else {
      this.tracingService.traceAction("create_KR", () => {
        this.createMetric();
      });
    }
  };

  private updateMetric(): void {
    if (!this.metric.insightName) {
      this.updateActualValueOnSave();
    }

    const options: GenerateMetricFromFormOptions = {
      actual: this.actual,
    };

    if (this.goal.locked && this.session.workflow?.editableKrFields?.length > 0) {
      options.workflowEditableKrFields = this.session.workflow.editableKrFields;
    }

    if (!this.form.controls.ownerIds.value || this.form.controls.ownerIds.value.length === 0) {
      options.goalOwnerIds = this.goal.ownerIds;
    }

    const formValue = this.form.getRawValue() as FormFieldsValues;
    const updatedFieldsPartial = createOnlyUpdatedFieldsMetric(this.metric, formValue, options);

    const keys = Object.keys(updatedFieldsPartial);
    if (keys.length === 1 && keys[0] === "id") {
      this.dismiss.emit();
      return;
    }

    this.metricsFacade
      .patchMetric$(updatedFieldsPartial)
      .pipe(
        takeUntil(this.destroyService),
        tap((metric: Metric) => {
          // GVS-19943 Only for editing dynamic KRs
          const shouldCheckin = this.metric.insightName && metric.initialValue !== undefined && metric.initialValue !== metric.actual && !metric.metricHistory?.length;
          if (shouldCheckin) {
            this.metricsFacade.publishCheckin({ metric, goal: this.goal, session: this.session });
          }
        })
      )
      .subscribe({
        next: (metric: Metric) => {
          this.afterSave(metric);
        },
        error: (error) => {
          this.indicators.saving = { error: error };
        },
      });
  }

  private createMetric(): void {
    const options: GenerateMetricFromFormOptions = {
      actual: this.actual,
    };

    if (!this.form.controls.ownerIds.value || this.form.controls.ownerIds.value.length === 0) {
      options.goalOwnerIds = this.goal.ownerIds;
    }

    const formValue = this.form.getRawValue() as FormFieldsValues;
    const newMetric: Metric = createNewMetricFromForm(this.metric, formValue, options);

    const shouldCheckin = newMetric.actual !== newMetric.initialValue;
    const gtmhubAdditionalParams = this.generateAdditionalPiReqParams();

    this.metricsFacade
      .createMetric$(newMetric, gtmhubAdditionalParams)
      .pipe(
        takeUntil(this.destroyService),
        tap((metric) => {
          if (shouldCheckin) {
            this.metricsFacade.publishCheckin({ metric, goal: this.goal, session: this.session });
          }
        })
      )
      .subscribe({
        next: (metric: Metric) => {
          this.sendRecommendationFeedback(metric);
          this.trackRecommendationHelpfulness(metric);
          this.afterSave(metric);
        },
        error: (error) => {
          this.indicators.saving = { error: error };
        },
      });
  }

  private updateActualValueOnSave(): void {
    // if metric type is changed
    if (this.metric.manualType !== this.form.controls.type.value.manualType) {
      // setting actual value to initial value if manual type is changed
      // this logic could be removed when backend has implemented such logic when patching metric
      this.actual = this.form.controls.initialValue.value;
      // else if metric type was not changed
      // but initial value is changed
    } else {
      // when editing number KRs if not checked in yet
      // if changing initial value -> change the actual value as well
      if (
        (this.form.controls.type.value.manualType === MetricManualTypeEnum.DOUBLE || this.form.controls.type.value.manualType === MetricManualTypeEnum.EMPTY) &&
        this.metric.metricHistory.length === 0 &&
        this.form.controls.initialValue.value !== this.metric.initialValue
      ) {
        // making actual value equal to the new initial value
        this.actual = this.form.controls.initialValue.value;
      }
    }
  }

  private afterSave = (metric: Metric): void => {
    delete this.indicators.saving;

    this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, {
      reason: "metricUpdated",
      detail: {
        metric: metric,
        goal: this.goal,
        session: this.session,
      },
    });

    this.broadcastService.emit("goalsChanged");
    this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, {
      reason: "goalChanged",
    });

    this.dismiss.emit();
  };

  private sendRecommendationFeedback = (metric: Metric): void => {
    if (this.prediction) {
      this.sendPredictionAnalyticsData(metric);
    }

    if (this.form.controls.automation.value.insightName) {
      this.autoInsightsFacade.sendPredictionAnalyticsFeedback(this.form.controls.automation.value);
      sendTrackingAnalyticsV2(this.analyticsService, this.form.controls.automation.value, "Key Result Insight Suggestion Created");
    }
  };

  private trackRecommendationHelpfulness(metric: Metric): void {
    // recommendation is helpful if user doesn't remove themselves as owner
    this.analyticsService.track(recommendation, {
      is_useful: metric.ownerIds.includes(getCurrentUserId()),
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_metric;owner",
    });
  }

  private sendPredictionAnalyticsData = (newMetric: Metric): void => {
    const data = generateMetricPredictionAnalytics(newMetric, this.prediction);
    // Do not add this.destroyService here, because we want to send the data even if the component is destroyed
    this.autoFieldsFacade.sendMetricAnalyticsData$(data).subscribe();
  };

  // SAVE SECTION END
  // AUTO FIELDS SUGGESTIONS SECTION START

  public updatePrediction = (propKey: MetricPredictedField, formField?: string): void => {
    const predictedKey = MetricPredictedFields.find((key) => key === propKey);

    if (predictedKey && this.form.controls[formField || predictedKey].value !== null) {
      this.shouldPredict[predictedKey] = false;
    }
  };

  private setAutopopulatedFields = (metricTitle: string): void => {
    this.broadcastService.emit("searchInsightSuggestions", { name: metricTitle });
    if (this.isEdit || this.isPrefilled) {
      return;
    }

    const noFieldsShouldBePredicted = Object.values(this.shouldPredict).every((v) => !v);

    if (noFieldsShouldBePredicted || this.form.controls.type.value.manualType === "boolean") {
      return;
    }

    if (this.autoFieldsFeatureEnabled) {
      this.autoFieldsFacade.getPredictionFromMetric$(metricTitle).subscribe({
        next: (prediction) => {
          this.prediction = prediction;
          this.updateAllPredictedMetrics(prediction);
        },
        error: () => {
          this.prediction = null;
        },
      });
    }
  };

  private updateAllPredictedMetrics = (prediction: IMetricPrediction): void => {
    for (const [field, shouldPredict] of Object.entries(this.shouldPredict)) {
      if (shouldPredict) {
        const defaultFieldValue = generateDefaultMetricValues(this.goal.ownerIds, this.stateParams.id)[field];

        if (field === "targetOperator") {
          this.form.controls.type.patchValue(
            {
              manualType: this.form.controls.type.value.manualType,
              targetOperator: prediction[field] || defaultFieldValue || null,
            },
            { emitEvent: false }
          );
        } else {
          this.form.controls[field].setValue(prediction[field] || defaultFieldValue || null, { emitEvent: false });
        }
      }
    }

    const shouldPredictInitialValue =
      this.shouldPredict.target && this.shouldPredict.initialValue && this.form.controls.type.value.targetOperator !== "at_most" && prediction.target;

    if (shouldPredictInitialValue) {
      this.prediction.initialValue = 0;
      this.form.controls.initialValue.setValue(0, { emitEvent: false });
    }

    this.cdr.detectChanges();
  };

  public handleTitleChange = (): void => {
    this.setSharedMetricServiceMetric();

    if (!this.form.controls.name.value) {
      return;
    }

    this.setAutopopulatedFields(this.form.controls.name.value);
  };

  // AUTO FIELDS SUGGESTIONS SECTION END
  // PLATFORM INTELLIGENCE SECTION START

  private checkSuggestionDrawerWhenCreateKrFeatureToggle(): Observable<boolean> {
    return this.piFeatureVisibilityFacade.isSuggestingKRInCreateKRFormVisible$().pipe(
      take(1),
      tap((enabled) => {
        this.isSuggestingKRInCreateKRFormEnabled = enabled;
      })
    );
  }

  public onSuggestionDrawerVisibilityChange = (isVisible: boolean): void => {
    this.shouldDisplaySuggestionDrawer = isVisible;
  };

  private generateAdditionalPiReqParams(): Record<"gtmhubAdditionalParams", Record<string, unknown>> {
    const hasAcceptedAnySuggestions = this.hasAcceptedTitleSuggestion || this.hasAcceptedDescriptionSuggestion;
    const shouldSendAdditionalParams = this.flowId && hasAcceptedAnySuggestions;

    return {
      ...(shouldSendAdditionalParams && { gtmhubAdditionalParams: { piFlowId: this.flowId, piFlowName: this.stateParams.flowName } }),
    };
  }

  public updateFormWithSuggestions(suggestions: PiSuggestionsType): void {
    if (suggestions) {
      for (const key of Object.keys(suggestions)) {
        if (key === "targetOperator") {
          const suggestion = {
            manualType: this.form.controls.type.value.manualType,
            targetOperator: suggestions[key] as MetricTargetOperator,
          };

          this.form.controls.type.setValue(suggestion, { emitEvent: false });
        } else {
          this.form.controls[key].setValue(suggestions[key], { emitEvent: false });
        }

        if (key === "name") {
          this.hasAcceptedTitleSuggestion = true;
        }

        if (key === "description") {
          this.hasAcceptedDescriptionSuggestion = true;
        }
      }
    }
  }

  // Should be called on loading and on upon updating any of these fields: name, description, ownerIds
  private setSharedMetricServiceMetric(): void {
    if (!this.isSuggestingKRInCreateKRFormEnabled) return;

    const newMetric = createNewMetricFromForm(this.metric, this.form.getRawValue(), { actual: this.actual });
    newMetric.sessionId = newMetric.sessionId || this.goal.sessionId;

    this.piSuggestionSharedMetricStateService.setMetric(newMetric);
    this.cdr.detectChanges();
  }

  // PLATFORM INTELLIGENCE SECTION END
}
